import { DocumentType, graphql } from '../gql';
import { getDocumentQueryKey } from '../query/hooks';
import { useOrganizationDomainContext } from '../contexts/OrganizationContext';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import request from 'graphql-request';
import config from '../config/config';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getEnvironmentDomain } from '../utils/getEnvironmentDomain';
import { useCookies } from 'react-cookie';
import { GetMeQuery } from '../gql/graphql';
import { useSnackbar } from '../store/snackbar';

const getMe = graphql(`
	query getMe {
		me {
			id
			name
			createdAt
			jobTitle

			avatar {
				url(width: 168, height: 168)
			}

			person {
				id
				name
				email
				organizations {
					id
					name
					roles
					slug
					slugAlias
					slugPreferred
					isCurrent
					roles
				}
				currentOrganization {
					id
					name
					slug
					slugAlias
					slugPreferred
					isCurrent
					roles
				}
				roles
			}
		}
	}
`);

type Me = DocumentType<typeof getMe>['me'];

const useMe = (): { meData?: GetMeQuery; isMeLoading: boolean } => {
	const [{ token, ood_impersonated_token }, , removeToken] = useCookies(['token', 'ood_impersonated_token']);
	const intl = useIntl();
	const queryClient = useQueryClient();
	const { organizationSlug } = useOrganizationDomainContext();
	const { openSnackbar } = useSnackbar();

	const {
		data: meData,
		isLoading: isMeLoading,
		error: meError,
	} = useQuery({
		queryKey: [getDocumentQueryKey(getMe)],
		queryFn: async () =>
			await request({
				url: `${config.apigw.base}/${organizationSlug}`,
				document: getMe,
				variables: {},
				requestHeaders: {
					Authorization: `Bearer ${ood_impersonated_token ?? token}`,
				},
			}),
		enabled: Boolean(token) || Boolean(ood_impersonated_token),
		staleTime: 60000,
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		if (meError && meError.message.includes('Authentication required')) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'auth.error' }),
				variant: 'alert',
				alert: {
					color: 'info',
				},
				close: true,
			});
			removeToken('token', { domain: getEnvironmentDomain() });
			removeToken('ood_impersonated_token', { domain: getEnvironmentDomain() });

			queryClient.clear();
			return;
		}
		if (meError) {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
			removeToken('token', { domain: getEnvironmentDomain() });
			removeToken('ood_impersonated_token', { domain: getEnvironmentDomain() });

			queryClient.clear();
			return;
		}
	}, [meError, intl, queryClient, removeToken, ood_impersonated_token, openSnackbar]);

	return { meData, isMeLoading };
};

export { useMe, type Me };
